import { CLIENT_DETAIL_TAB_VALUES, PAGES } from "@dune/types";
import { Button } from "@dune/ui";
import { ArrowLeft } from "@untitled-ui/icons-react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetClientById } from "../../../api/queries/clients.queries";
import ConstructionSitesTab from "../../../component/clients/tabs/ConstructionSitesTab";
import ContractsTab from "../../../component/clients/tabs/ContractsTab";
import GeneralTab from "../../../component/clients/tabs/GeneralTab";
import PropositionsTab from "../../../component/clients/tabs/PropositionsTab";
import ReceiptsTab from "../../../component/clients/tabs/ReceiptsTab";
import ErrorBoundary from "../../../component/common/ErrorBoundary";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutSkeleton,
	TabLayoutErrorSkeleton,
} from "../../../component/layout/PageLayoutWithTabs";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { EditClientDialog } from "./components/dialogs/EditClientDialog";
import { isSocietySyndic } from "@dune/utils";

const ClientDetailsPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useRequiredParams<{ id: string }>();
	const [clientType] = useQueryState("clientType", parseAsString);
	const {
		data: client,
		isLoading,
		error,
	} = useGetClientById(id, clientType || undefined);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

	const tabs: Tab[] = [
		{ value: CLIENT_DETAIL_TAB_VALUES.GENERAL, label: "Général" },
		{ value: CLIENT_DETAIL_TAB_VALUES.CONSTRUCTION_SITES, label: "Chantiers" },
		{ value: CLIENT_DETAIL_TAB_VALUES.PROPOSALS, label: "Propositions" },
		{ value: CLIENT_DETAIL_TAB_VALUES.CONTRACTS, label: "Contrats" },
		{ value: CLIENT_DETAIL_TAB_VALUES.RECEIPTS, label: "Quittances" },
	];

	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault(CLIENT_DETAIL_TAB_VALUES.GENERAL),
	);

	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !client) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations du client."
				tabCount={tabs.length}
			/>
		);
	}

	const getPageTitle = () => {
		if (client.user) {
			return `${client.user.firstname || ""} ${client.user.lastname || ""}`.trim();
		}
		if (client.society) {
			return isSocietySyndic(client.society) ? "Syndic" : "Société";
		}
		return "";
	};

	const tabContent: { [key: string]: React.ReactNode } = {
		[CLIENT_DETAIL_TAB_VALUES.GENERAL]: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des informations générales.">
				<GeneralTab client={client} setIsEditDialogOpen={setIsEditDialogOpen} />
			</ErrorBoundary>
		),
		[CLIENT_DETAIL_TAB_VALUES.CONSTRUCTION_SITES]: (
			<ConstructionSitesTab client={client} />
		),
		[CLIENT_DETAIL_TAB_VALUES.PROPOSALS]: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des propositions.">
				<PropositionsTab client={client} />
			</ErrorBoundary>
		),
		[CLIENT_DETAIL_TAB_VALUES.CONTRACTS]: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des contrats.">
				<ContractsTab client={client} />
			</ErrorBoundary>
		),
		[CLIENT_DETAIL_TAB_VALUES.RECEIPTS]: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des quittances.">
				<ReceiptsTab client={client} />
			</ErrorBoundary>
		),
	};

	return (
		<>
			<PageLayoutWithTabs
				title={getPageTitle()}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				tabContent={tabContent}
				topActions={
					<Button
						variant="ghost"
						onClick={() => navigate(PAGES.CLIENTS.index)}
						className="has-[>svg]:px-1 text-text-blue"
					>
						<ArrowLeft />
						Retour
					</Button>
				}
			/>

			<EditClientDialog
				open={isEditDialogOpen}
				onOpenChange={setIsEditDialogOpen}
				client={client}
			/>
		</>
	);
};

export default ClientDetailsPage;
